/**
 * Import groups: the field catalogue a CSV is mapped onto.
 *
 * The mapping screen used to know only about people. Payroll needs the same
 * screen against a different set of columns, so the catalogue moved out of
 * the people feature and became data keyed by a group. The key matches the
 * handler key the shore registers with its `ImportExtensionRegistry`, so a
 * group name means the same thing on both sides of the wire.
 *
 * Adding a group is adding an entry here plus its key in `ImportGroupKey`.
 * The people catalogue is long enough to live in its own file
 * (`people-import-group.ts`).
 */

import type { Permission } from "@/shared/auth/permissions";
import type { FeatureKey } from "@/shared/entitlements/entitlements.constants";
import { PEOPLE_GROUP } from "@/shared/import/people-import-group";

/// Visual grouping on the mapping screen. Purely presentational: a group
/// whose fields carry no section renders as one flat list.
export type ImportSectionKey =
  | "basics"
  | "job"
  | "personal"
  | "exit"
  | "workLocation"
  | "compensation"
  | "bankStatutory";

/// A second canonical column the rewritten CSV carries with the same values
/// as the field it hangs off. `requires` gates it independently of the field.
export interface ImportColumnCopy {
  readonly name: string;
  readonly requires?: Permission;
}

export interface ImportField {
  /** Canonical column name the shore's parser recognises. */
  readonly name: string;
  readonly displayName: string;
  readonly required: boolean;
  readonly section?: ImportSectionKey;
  /** Offered (and sent) only when the caller holds this permission. */
  readonly requires?: Permission;
  /**
   * Offered only when the ACCOUNT's plan has this feature. Mirrors the
   * `requiredEntitlement` the shore's handler for this column declares: a
   * file carrying the column on a plan without it fails with 403
   * PLAN_UPGRADE_REQUIRED, whatever the caller's permissions.
   */
  readonly requiresFeature?: FeatureKey;
  readonly alsoSendAs?: readonly ImportColumnCopy[];
}

export interface ImportGroup {
  readonly key: ImportGroupKey;
  readonly fields: readonly ImportField[];
  /** Header spellings seen in the wild, normalised before comparison. */
  readonly aliases: Readonly<Record<string, readonly string[]>>;
  /**
   * Spellings matched only when a header equals them, never by containment.
   * For abbreviations short enough to hide inside unrelated headers ("pan"
   * inside "Company").
   */
  readonly exactAliases?: Readonly<Record<string, readonly string[]>>;
  /** Shown under the mapping heading. */
  readonly hint: string;
  /** Shown under the file picker: which columns the handler needs. */
  readonly uploadHint: string;
  /** Shown once every required field is mapped. */
  readonly readyMessage: string;
  /** Shown while a required field is still unmapped. */
  readonly missingMessage: string;
  readonly confirmLabel: string;
  /** Confirm button text while the caller's confirm is in flight. */
  readonly pendingLabel?: string;
}

export type ImportGroupKey = "people" | "payroll";

/**
 * PAYD-FR-003. The payroll columns claimed by the shore's `PAYROLL_ROW_COLUMNS`,
 * `PAYROLL_HOLD_COLUMNS` and `PAYROLL_PAYMENT_DAYS_COLUMNS`
 * (`payroll-import-columns.ts`), mirrored exactly: the same canonical names,
 * on the same `/imports/csv` route the people wizard uses (there is no
 * payroll import route, see `payroll-import.schema.ts`). `email` is required
 * because a payroll cell with nobody to land on is not a row, it is orphaned
 * money; every payroll field is optional so a file can carry only the
 * columns it has.
 *
 * `payAction`, `payActionReason`, `payActionWindowEnd`, `openingTaxableSalary`,
 * `openingTdsDeducted` and `openingMonthsElsewhere` are declared on the shore
 * but REFUSED, on purpose, by `PAYROLL_REFUSED_HEADERS`: a file naming a pay
 * action has no roster row to attach it to, and an opening balance has no
 * table to land in yet. They are deliberately absent from this catalogue too,
 * so this screen never offers a column the commit will refuse.
 */
const PAYROLL_GROUP: ImportGroup = {
  key: "payroll",
  fields: [
    { name: "email", displayName: "Email", required: true },
    { name: "salaryComponent", displayName: "Component abbreviation", required: false },
    { name: "amount", displayName: "Amount", required: false },
    { name: "currency", displayName: "Currency", required: false },
    { name: "payrollDate", displayName: "Payroll date", required: false },
    { name: "fromDate", displayName: "From date", required: false },
    { name: "toDate", displayName: "To date", required: false },
    { name: "taxTiming", displayName: "Tax timing", required: false },
    { name: "amountReason", displayName: "Reason", required: false },
    { name: "holdFromDate", displayName: "Hold from date", required: false },
    { name: "holdCycles", displayName: "Hold cycles", required: false },
    { name: "holdMode", displayName: "Hold mode", required: false },
    { name: "holdReason", displayName: "Hold reason", required: false },
    { name: "cycleStart", displayName: "Payment days cycle", required: false },
    { name: "lwpDays", displayName: "LWP days", required: false },
    { name: "absentDays", displayName: "Absent days", required: false },
    { name: "paymentDaysReason", displayName: "Payment days reason", required: false },
  ],
  aliases: {
    email: ["email", "emailaddress", "mail"],
    salaryComponent: ["salarycomponent", "component", "componentabbreviation", "abbreviation"],
    amount: ["amount", "salaryamount"],
    currency: ["currency"],
    payrollDate: ["payrolldate"],
    fromDate: ["fromdate"],
    toDate: ["todate"],
    taxTiming: ["taxtiming", "taxtreatment"],
    amountReason: ["amountreason", "reason"],
    holdFromDate: ["holdfromdate"],
    holdCycles: ["holdcycles"],
    holdMode: ["holdmode"],
    holdReason: ["holdreason"],
    cycleStart: ["cyclestart", "paymentdayscycle"],
    lwpDays: ["lwpdays", "lopdays"],
    absentDays: ["absentdays"],
    paymentDaysReason: ["paymentdaysreason"],
  },
  uploadHint:
    "Required column: email, to say who the row is for. Any extras column we recognise (case-insensitive) maps automatically. A column that says whether to pay or hold someone, or that sets an opening tax balance from another employer, cannot be imported yet and will fail the file.",
  hint: "We auto-matched what we could. Every field but email is optional. Map only the extras, holds, or payment-days columns your file carries.",
  readyMessage:
    "Looks good. Click Import to write these extras into the cycle. Existing matching rows are updated; new people-and-amount rows are added.",
  missingMessage: "Map the Email column before continuing, every row needs one.",
  confirmLabel: "Import",
};

const IMPORT_GROUPS: Readonly<Record<ImportGroupKey, ImportGroup>> = {
  people: PEOPLE_GROUP,
  payroll: PAYROLL_GROUP,
};

export function importGroup(key: ImportGroupKey): ImportGroup {
  return IMPORT_GROUPS[key];
}
